import { useHistory, useRouteMatch } from '@fs/zion-router'
import { Row, H6 } from '@fs/zion-ui'
import React, { useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../app/PageTitleContext'
import useArtifactSearchResults from '../../hooks/artifacts/useArtifactSearchResults'
import ArtifactCard from './ArtifactCard'
import ArtifactCardGrid from './ArtifactCardGrid'
import SearchBar from './SearchBar'
import CategoryFilter from './filter/CategoryFilter'
import { DEFAULT_PAGE_SIZE } from './util/artifactsConstants'
import DmcPaginator from '../common/DmcPaginator'

/**
 * Page that displays artifact search results (same results as “Find” page on Memories).
 * Has a search bar, pagination, and category filter. Always uses the default page size (100),
 * since the backend doesn’t allow >100 results with the current search engine.
 */
export default function SearchArtifacts() {
  usePageView({ page_detail: 'DMConsole:SearchArtifacts' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.artifacts.search'))
  const MAX_RESULTS = 50
  const history = useHistory()
  const match = useRouteMatch()
  const searchTerms = match.params.searchTerms ? match.params.searchTerms : ''
  const artifactCategory = match.params.artifactCategory ? match.params.artifactCategory : 'any'
  const [index, setIndex] = useState(0)
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_SIZE)
  const { artifacts, totalArtifactsSpanningPages } = useArtifactSearchResults({
    searchTerms,
    artifactCategory: artifactCategory.toUpperCase(),
    paginatorOffset: index * pageCount,
    paginatorCount: pageCount,
  })
  let content = null
  if (artifacts) {
    content = (
      <DmcPaginator
        countKey="SearchForArtifactsByKeyword"
        maxPageCount={MAX_RESULTS}
        length={totalArtifactsSpanningPages}
        pageCount={pageCount}
        setPageCount={setPageCount}
        index={index}
        setIndex={setIndex}
        topAndBottom
      >
        <ArtifactCardGrid>
          {artifacts.map((artifact) => {
            return artifact.first ? <ArtifactCard key={artifact.id} artifact={artifact} /> : null
          })}
        </ArtifactCardGrid>
      </DmcPaginator>
    )
  }
  return (
    <>
      <SearchBar defaultQuery={searchTerms} />
      <Row>
        {artifacts && (
          <H6 growX alignY="nextToInput">
            {totalArtifactsSpanningPages > 0
              ? t('artifacts.keywordSearchResultsCount_plural', 'About {count} results', {
                  count: totalArtifactsSpanningPages,
                })
              : t('artifacts.noArtifactsFound', 'No artifacts found')}
          </H6>
        )}
        {artifacts && (
          <CategoryFilter
            value={artifactCategory}
            onChange={(newCategory) => {
              history.push(`/artifacts/search/${searchTerms}/${newCategory}`)
            }}
          />
        )}
      </Row>
      {content}
    </>
  )
}
